import { useQuery } from "@tanstack/react-query";
import { MAX_PROFILE_IMAGE_URL_BATCH_SIZE } from "@api/shared-runtime/profile-image";
import { MEDIA_DOWNLOAD_TTL_SECONDS } from "@api/shared-runtime/signed-url-ttl";
import { trpcClient } from "@/trpc";
import { createProfileImageUrlBatcher } from "@/features/profile/hooks/profile-image-url-batcher";

const PROFILE_IMAGE_URL_QUERY_KEY = "profile-image-url";

/**
 * Stop reusing a signed URL this long before the server-issued URL expires, so
 * an avatar that mounts late in the window still gets a link that loads.
 */
const EXPIRY_MARGIN_SECONDS = 10 * 60;
const MILLISECONDS_PER_SECOND = 1000;
const PROFILE_IMAGE_URL_FRESH_MS =
  (MEDIA_DOWNLOAD_TTL_SECONDS - EXPIRY_MARGIN_SECONDS) *
  MILLISECONDS_PER_SECOND;

// One batcher for the app, so avatars anywhere on the page share a call.
const loadProfileImageUrl = createProfileImageUrlBatcher({
  fetchUrls: async (userIds) => {
    const { urls } = await trpcClient.profile.getImageUrls.query({ userIds });
    return urls;
  },
  maxBatchSize: MAX_PROFILE_IMAGE_URL_BATCH_SIZE,
});

/** Query key prefix for one user's avatar URL, for invalidation after upload. */
export function profileImageUrlQueryKey(userId: string) {
  return [PROFILE_IMAGE_URL_QUERY_KEY, userId];
}

/**
 * A signed URL for the user's profile image, or `null` while loading, when
 * they have none, or when the viewer may not see it. Concurrent avatars are
 * fetched in one API call and cached for most of the URL's lifetime.
 */
export function useProfileImageUrl({
  userId,
  profileImagePath,
}: {
  userId: string;
  profileImagePath: string | null | undefined;
}): string | null {
  const { data } = useQuery({
    // The path is part of the key so setting a first image fetches its URL.
    queryKey: [...profileImageUrlQueryKey(userId), profileImagePath ?? null],
    queryFn: () => loadProfileImageUrl(userId),
    enabled: Boolean(profileImagePath),
    staleTime: PROFILE_IMAGE_URL_FRESH_MS,
    gcTime: PROFILE_IMAGE_URL_FRESH_MS,
  });
  return data ?? null;
}
